import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser } from "lucide-react";
import cn from "./cn";
import AppButton from "./AppButton";

const INKS = [
  { value: "#111827", label: "Black ink" },
  { value: "#1e3a8a", label: "Blue ink" },
];

/**
 * SignaturePad — a box to sign in with a mouse, a finger or a stylus, drawn on
 * a transparent canvas so the PNG it becomes sits on whatever paper prints it:
 * the invoice's authorised signatory, a student's placement agreement.
 *
 * `touch-action: none` keeps a finger stroke from scrolling the page. The pad
 * is not keyboard-operable by nature; a caller that needs an accessible path
 * offers an upload beside it, which is.
 *
 * Ref: { canvas, isEmpty(), clear() }. `onChange(hasInk)` after every stroke.
 */
const SignaturePad = forwardRef(function SignaturePad({ onChange, height = 170 }, ref) {
  const canvasRef = useRef(null);
  const state = useRef({ drawing: false, last: null, mid: null, ink: false });
  const [ink, setInk] = useState(INKS[0].value);
  const [hasInk, setHasInk] = useState(false);

  // Size the drawing surface to the box at the screen's pixel density, once —
  // resizing a canvas clears it.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || 480;
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    const ctx = canvas.getContext("2d");
    if (ctx) ctx.scale(ratio, ratio);
  }, [height]);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
    }
    state.current.ink = false;
    setHasInk(false);
    onChange?.(false);
  }, [onChange]);

  useImperativeHandle(ref, () => ({
    get canvas() { return canvasRef.current; },
    isEmpty: () => !state.current.ink,
    clear,
  }), [clear]);

  const pointOf = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top, pressure: e.pressure || 0.5 };
  };

  const strokeTo = (ctx, from, control, to, pressure) => {
    ctx.strokeStyle = ink;
    ctx.lineWidth = 1.6 + pressure * 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.beginPath();
    ctx.moveTo(from.x, from.y);
    ctx.quadraticCurveTo(control.x, control.y, to.x, to.y);
    ctx.stroke();
  };

  const onPointerDown = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    canvas.setPointerCapture?.(e.pointerId);
    const p = pointOf(e);
    state.current = { ...state.current, drawing: true, last: p, mid: p };
    // A tap is a dot.
    ctx.fillStyle = ink;
    ctx.beginPath();
    ctx.arc(p.x, p.y, 1.4, 0, Math.PI * 2);
    ctx.fill();
  };

  const onPointerMove = (e) => {
    const s = state.current;
    if (!s.drawing) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const p = pointOf(e);
    const mid = { x: (s.last.x + p.x) / 2, y: (s.last.y + p.y) / 2 };
    strokeTo(ctx, s.mid, s.last, mid, p.pressure);
    s.last = p;
    s.mid = mid;
    if (!s.ink) {
      s.ink = true;
      setHasInk(true);
    }
  };

  const onPointerUp = () => {
    if (!state.current.drawing) return;
    state.current.drawing = false;
    onChange?.(state.current.ink);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {/* The pad is PAPER in every theme (`--paper-*`, the print tokens), never the
          card surface: the ink is black or blue, and on a dark card a black
          signature could not be seen while it was being drawn. The PNG is
          transparent either way, so what prints is unchanged. */}
      <div className="tw-relative tw-overflow-hidden tw-rounded-md tw-border tw-border-dashed tw-border-line tw-bg-[var(--paper)]">
        <canvas
          ref={canvasRef}
          className="tw-block tw-w-full tw-cursor-crosshair tw-touch-none"
          style={{ height }}
          aria-label="Signature pad — sign here with your mouse, finger or stylus"
          role="img"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onPointerLeave={onPointerUp}
        />
        <span
          className="tw-pointer-events-none tw-absolute tw-inset-x-6 tw-bottom-9 tw-h-px tw-bg-[var(--paper-line)]"
          aria-hidden="true"
        />
        {!hasInk && (
          <span className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-1/2 tw--translate-y-1/2 tw-text-center tw-text-sm tw-text-[var(--paper-ink-muted)]">
            Sign here
          </span>
        )}
      </div>
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <div className="tw-flex tw-items-center tw-gap-2" role="group" aria-label="Ink colour">
          {INKS.map((option) => (
            <button
              key={option.value}
              type="button"
              aria-label={option.label}
              aria-pressed={ink === option.value}
              onClick={() => setInk(option.value)}
              className={cn(
                "tw-h-6 tw-w-6 tw-cursor-pointer tw-rounded-full tw-border-2 tw-border-solid tw-p-0 tw-focus-ring",
                ink === option.value ? "tw-border-primary" : "tw-border-line",
              )}
              style={{ backgroundColor: option.value }}
            />
          ))}
        </div>
        <AppButton variant="ghost" size="sm" onClick={clear} disabled={!hasInk}>
          <Eraser size={14} /> Clear
        </AppButton>
      </div>
    </div>
  );
});

export default SignaturePad;
